import React from 'react';
import { KTSVG } from 'src/_metronic/helpers';

// Sunucu tarafı AI benzerlik kontrolü sonucu (create / update yanıtı approval_required dönerse).
// Potansiyel müşteri EKLE ve DÜZENLE (ad değişikliği) modalları ortak kullanır.
export type ApprovalMatchItem = {
  kind: 'potential' | 'cari' | 'customer';
  ref: string;
  name: string;
  owner?: string | null;
  score?: number;
  confidence?: number;
  reason?: string;
  matched_by?: string;
  extra?: Record<string, any> | null;
};

export type ApprovalInfo = {
  request_id: number;
  already_pending: boolean;
  matches: ApprovalMatchItem[];
  summary: string | null;
  ai_status: string;
  ai_model: string | null;
};

// Duotone KTSVG ikonları (bootstrap-icons projede yüklü değil — bi sınıfları görünmez)
export const PANEL_ICONS = {
  ai: '/media/icons/duotone/Devices/CPU1.svg',
  warning: '/media/icons/duotone/Code/Warning-2.svg',
  doneCircle: '/media/icons/duotone/Code/Done-circle.svg',
  search: '/media/icons/duotone/General/Search.svg',
  star: '/media/icons/duotone/General/Star.svg',
  group: '/media/icons/duotone/Communication/Group.svg',
  building: '/media/icons/duotone/Home/Building.svg',
  shieldCheck: '/media/icons/duotone/General/Shield-check.svg',
  barcode: '/media/icons/duotone/Shopping/Barcode-scan.svg',
  contact: '/media/icons/duotone/Communication/Contact1.svg',
  questionCircle: '/media/icons/duotone/Code/Question-circle.svg',
};

export const APPROVAL_KIND_LABELS: Record<string, { label: string; badge: string; icon: string; color: string }> = {
  potential: { label: 'Potansiyel', badge: 'badge-light-info', icon: PANEL_ICONS.group, color: 'info' },
  cari: { label: 'DIA Cari', badge: 'badge-light-primary', icon: PANEL_ICONS.building, color: 'primary' },
  customer: { label: 'Müşteri', badge: 'badge-light-success', icon: PANEL_ICONS.shieldCheck, color: 'success' },
};

// Aday potansiyel kaydın canlı mı yoksa kapanmış mı olduğunu gösteren rozet.
// Benzerlik taraması iptal/pasif kayıtları BİLEREK kapsar (firmanın geçmişi
// kanıttır), ama listede üç birebir ad yan yana görününce hangisinin gerçekten
// birinin üzerinde olduğu anlaşılmıyordu — durum rozeti bunu ayırır.
export const matchStatusBadge = (m: { kind: string; extra?: Record<string, any> | null }) => {
  if (m.kind !== 'potential' || !m.extra) return null;
  const ls = String(m.extra.lifecycle_status || '');
  const ap = String(m.extra.approval_status || '');
  if (m.extra.is_active === false) {
    if (ap === 'rejected') return { label: 'Onayı reddedilmiş', badge: 'badge-light-danger' };
    if (ls === 'cancelled') return { label: 'İptal edilmiş kayıt', badge: 'badge-light-danger' };
    if (ls === 'dismissed') return { label: 'İlgilenilmiyor', badge: 'badge-light-secondary' };
    return { label: 'Pasif kayıt', badge: 'badge-light-secondary' };
  }
  // Aday kayıt henüz yönetici onayından geçmediyse bunu açıkça söyle (Faruk'ların
  // BASF vakası, 3 Kas 2026): aynı firmayı iki satışçı aynı anda eklerse ikincisi
  // ilkinin beklediğini görmeli.
  if (ap === 'pending' || ap === 'scanning') return { label: 'Onay bekliyor', badge: 'badge-light-warning' };
  if (ls === 'assigned') return { label: 'Aktif — atanmış', badge: 'badge-light-success' };
  if (ls === 'expired' || ls === 'unassigned') return { label: 'Süresi dolmuş / sahipsiz', badge: 'badge-light-warning' };
  return { label: 'Aktif', badge: 'badge-light-success' };
};

// Benzerlik yüzdesine göre renk (kart ölçerleri için)
export const severityColor = (pct: number) => (pct >= 80 ? 'danger' : pct >= 60 ? 'warning' : 'info');

// Eşleşme kartları (modallarda ve yönetici düzeltilen ad uyarısında ortak)
export const SimilarityMatchCards: React.FC<{ matches: ApprovalMatchItem[]; maxHeight?: number }> = ({ matches, maxHeight = 280 }) => (
  <div className="d-flex flex-column gap-2" style={{ maxHeight, overflowY: 'auto' }}>
    {matches.map((m, idx) => {
      const kindInfo = APPROVAL_KIND_LABELS[m.kind] || { label: m.kind, badge: 'badge-light', icon: PANEL_ICONS.questionCircle, color: 'secondary' };
      const pct = Math.round(((m.confidence ?? m.score) || 0) * 100);
      const sev = severityColor(pct);
      const st = matchStatusBadge(m);
      return (
        <div key={idx} className="d-flex align-items-center border border-gray-200 rounded p-3 bg-white">
          <span className="symbol symbol-40px me-3 flex-shrink-0">
            <span className={`symbol-label bg-light-${kindInfo.color}`}>
              <KTSVG path={kindInfo.icon} className={`svg-icon-3 svg-icon-${kindInfo.color}`} />
            </span>
          </span>
          <div className="flex-grow-1 min-w-0">
            <div className="d-flex align-items-center gap-2 flex-wrap">
              <span className="fw-bold text-gray-800 fs-7">{m.name}</span>
              <span className={`badge badge-sm ${kindInfo.badge}`}>{kindInfo.label}</span>
              {m.matched_by === 'vkn' && (
                <span className="badge badge-sm badge-danger d-inline-flex align-items-center gap-1">
                  <KTSVG path={PANEL_ICONS.barcode} className="svg-icon-7 svg-icon-white" />
                  VKN eşleşmesi
                </span>
              )}
              {m.matched_by === 'word' && <span className="badge badge-sm badge-light-warning">Kısaltma/kelime eşleşmesi</span>}
              {st ? <span className={`badge badge-sm ${st.badge}`}>{st.label}</span> : null}
            </div>
            <div className="d-flex align-items-center gap-3 mt-1 flex-wrap">
              {m.owner && (
                <span className="text-gray-600 fs-8 d-inline-flex align-items-center gap-1">
                  <KTSVG path={PANEL_ICONS.contact} className="svg-icon-6" />
                  {m.owner}
                </span>
              )}
              {m.reason && <span className="text-gray-600 fs-8">{m.reason}</span>}
            </div>
          </div>
          <div className="text-end ms-3 flex-shrink-0" style={{ width: 90 }}>
            <div className={`fw-bold fs-6 text-${sev}`}>%{pct}</div>
            <div className="progress h-6px bg-light-secondary">
              <div className={`progress-bar bg-${sev}`} style={{ width: `${pct}%` }}></div>
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

interface PanelProps {
  info: ApprovalInfo;
  title: React.ReactNode;
  description: React.ReactNode;
  matchesTitle?: string;
}

/**
 * Onay paneli üst bloğu: durum kartı + AI değerlendirmesi + eşleşme kartları.
 * ai_status 'no_candidates' (ad değişikliğinde benzerlik bulunmadı ama politika
 * gereği onaya düşüyor) ayrıca ele alınır — "AI erişilemedi" sanılmasın.
 */
export const SimilarityApprovalPanel: React.FC<PanelProps> = ({ info, title, description, matchesTitle = 'Benzer bulunan kayıtlar' }) => {
  const noCandidates = info.ai_status === 'no_candidates';
  return (
    <>
      {/* Üst durum kartı */}
      <div className={`d-flex align-items-center rounded border border-dashed p-4 mb-5 ${noCandidates ? 'bg-light-success border-success' : 'bg-light-warning border-warning'}`}>
        <span className="symbol symbol-50px me-4 flex-shrink-0">
          <span className={`symbol-label ${noCandidates ? 'bg-success' : 'bg-warning'}`}>
            <KTSVG path={PANEL_ICONS.ai} className="svg-icon-2x svg-icon-white" />
          </span>
        </span>
        <div>
          <div className="fw-bold fs-5 text-gray-900">{title}</div>
          <div className="fs-7 text-gray-700 mt-1">{description}</div>
          <div className="d-flex gap-2 flex-wrap mt-2">
            {noCandidates ? (
              <span className="badge badge-light-success d-inline-flex align-items-center gap-1">
                <KTSVG path={PANEL_ICONS.doneCircle} className="svg-icon-7 svg-icon-success" />
                Benzer kayıt bulunmadı
              </span>
            ) : (
              <span className="badge badge-light d-inline-flex align-items-center gap-1">
                <KTSVG path={PANEL_ICONS.search} className="svg-icon-7" />
                {info.matches.length} eşleşme
              </span>
            )}
            {info.ai_model && (
              <span className="badge badge-light-primary d-inline-flex align-items-center gap-1">
                <KTSVG path={PANEL_ICONS.ai} className="svg-icon-7 svg-icon-primary" />
                {info.ai_model}
              </span>
            )}
            {info.ai_status === 'quick_hard' ? (
              <span className="badge badge-light-warning d-inline-flex align-items-center gap-1">
                <KTSVG path={PANEL_ICONS.search} className="svg-icon-7 svg-icon-warning" />
                Hızlı kontrol: VKN / ünvan birebir eşleşmesi
              </span>
            ) : (
              !noCandidates &&
              info.ai_status !== 'ai_flagged' && (
                <span className="badge badge-light-danger d-inline-flex align-items-center gap-1">
                  <KTSVG path={PANEL_ICONS.warning} className="svg-icon-7 svg-icon-danger" />
                  AI erişilemedi — deterministik eşleşmeler
                </span>
              )
            )}
          </div>
        </div>
      </div>

      {/* AI değerlendirmesi */}
      {info.summary && (
        <div className="border-start border-4 border-warning bg-light rounded-end ps-4 pe-3 py-3 mb-5">
          <div className="fs-8 text-gray-500 fw-bold mb-1 d-flex align-items-center gap-1">
            <KTSVG path={PANEL_ICONS.star} className="svg-icon-6 svg-icon-warning" />
            AI DEĞERLENDİRMESİ
          </div>
          <div className="fs-7 text-gray-800 fst-italic">{info.summary}</div>
        </div>
      )}

      {/* Eşleşme kartları */}
      {info.matches.length > 0 && (
        <div className="mb-5">
          <div className="fw-bold text-gray-800 mb-3">{matchesTitle}</div>
          <SimilarityMatchCards matches={info.matches} />
        </div>
      )}
    </>
  );
};

export default SimilarityApprovalPanel;
